import { Alert, Button, Group, Select, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  ErrorState,
  InlineError,
  LoadingSkeleton,
  MoneyField,
  SectionCard,
  StickyActionBar,
} from "../../components/ui";
import { api, type BillDetail, type DeliveryAgent, type OrderDetail } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";
import { BillView } from "./BillView";

/** Only these roles may enter a discount; the server enforces it too. */
const DISCOUNT_ROLES = ["BILLING_EXECUTIVE", "STORE_MANAGER", "ADMIN"];

/**
 * Bill generation (REQ-SALES-3, REQ-INV-1).
 *
 * This screen is the only place in the app that moves stock, and the server
 * refuses a second bill for the same order - so the preview below is computed
 * locally purely to show the operator what they are about to commit to. Every
 * figure displayed AFTER generation comes back from the server, which is the
 * authority: it recomputes in integer paise and the preview is only ever an
 * estimate for the eye.
 *
 * The discount is optional (the user was explicit: "discount maybe given or
 * not"), entered per line, and in rupees off that line's total.
 */
export function BillGenerate() {
  const { orderId = "" } = useParams();
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [order, setOrder] = useState<OrderDetail>();
  const [discounts, setDiscounts] = useState<Record<string, number | string>>({});
  const [bill, setBill] = useState<BillDetail>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [agents, setAgents] = useState<DeliveryAgent[]>();
  /** "" means nobody - the delivery waits for the Store Manager. */
  const [deliveryAgentId, setDeliveryAgentId] = useState("");

  const mayDiscount = hasRole(...DISCOUNT_ROLES);

  useEffect(() => {
    void (async () => {
      try {
        const loaded = await api.order(orderId);
        setOrder(loaded.order);

        // WHOEVER THE SERVICE MANAGER NAMED, pre-selected and changeable.
        //
        // This used to pre-select the agent who COLLECTED the battery, which
        // the server also defaulted to. Both are gone: the business moved
        // service assignment to the Service Manager, who now names the agent
        // at the outcome step, and that name arrives on the order.
        //
        // Pre-selected rather than merely displayed, because the business
        // asked for the Billing Executive to be able to override it - they
        // are the last person to look at the order before it leaves. Changing
        // it is a real choice; the audit trail records which of the two made
        // it.
        const intended = loaded.order.intendedDeliveryAgent;
        if (intended && intended.status === "ACTIVE") {
          setDeliveryAgentId(intended.id);
        }
        // An order already billed is not a failure state - show the bill.
        if (loaded.order.bill) {
          const existing = await api.bill(loaded.order.bill.id);
          setBill(existing.bill);
        }
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not load the order.");
      }
    })();
  }, [orderId]);

  useEffect(() => {
    void (async () => {
      try {
        setAgents((await api.deliveryAgents()).agents);
      } catch {
        // A failure here must not block billing - the delivery can be handed
        // out by the Store Manager afterwards. The picker simply says so.
        setAgents([]);
      }
    })();
  }, []);

  /**
   * Local preview only. Mirrors pricing.service: discount comes off the line
   * before GST, and an intra-state sale splits the tax into equal halves.
   *
   * THE DISCOUNT IS NOT WORKED OUT HERE WHEN THE RULE IMPOSES ONE.
   *
   * This used to compute quantity x unit price whatever the order was, so a
   * warranty replacement - which bills nothing - previewed a full charge with
   * GST right up until the moment it was generated. Anyone reading it would
   * reasonably believe they were about to invoice a customer for a battery
   * being replaced free.
   *
   * It was fixed once by reading order.billsZero, and a pro-rata order would
   * have walked straight back into it: not zero-rated, no typed discount, so
   * the preview would again have shown full price for an order that bills an
   * amount a Service Manager agreed. So the server now states the discount it
   * WILL apply per line (imposedDiscount) and this renders that figure. There
   * is no copy of the apportionment here to drift.
   */
  const preview = useMemo(() => {
    if (!order) return undefined;
    const intraState = order.gstTreatment === "INTRA_STATE";
    const billsZero = order.billsZero;
    const agreed = order.billsAgreedAmount;

    let subTotal = 0;
    let discountTotal = 0;
    let tax = 0;
    const rows = order.lineItems.map((line) => {
      const gross = Number(line.unitPrice) * line.quantityFulfilled;
      const typed = Number(discounts[line.id] ?? line.discountAmount);
      // Stated by the server for a zero-rated or pro-rata order; the line's
      // own only when the rule leaves the decision open.
      const discount =
        line.imposedDiscount !== null
          ? Number(line.imposedDiscount)
          : Number.isFinite(typed) && typed > 0
            ? typed
            : 0;
      const taxable = Math.max(0, gross - discount);
      const lineTax = (taxable * Number(line.gstRate)) / 100;
      subTotal += gross;
      discountTotal += Math.min(discount, gross);
      tax += lineTax;
      return {
        line,
        gross,
        discount,
        taxable,
        lineTax,
        excessive: line.imposedDiscount === null && discount > gross,
      };
    });

    const taxable = Math.max(0, subTotal - discountTotal);
    return {
      rows,
      subTotal,
      discountTotal,
      taxable,
      tax,
      total: taxable + tax,
      intraState,
      billsZero,
      agreed,
      excessive: rows.some((row) => row.excessive),
    };
  }, [order, discounts]);

  async function generate() {
    if (!order || !preview || preview.excessive || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const entered = order.lineItems
        .map((line) => ({
          lineItemId: line.id,
          discountAmount: Number(discounts[line.id] ?? "0"),
        }))
        .filter((entry) => Number.isFinite(entry.discountAmount) && entry.discountAmount > 0);

      const generated = await api.generateBill({
        orderId: order.id,
        ...(entered.length > 0 ? { discounts: entered } : {}),
        ...(deliveryAgentId ? { deliveryAgentId } : {}),
      });
      setBill(generated.bill);
    } catch (caught) {
      // Deliberately does NOT clear the form or retry: if the server took the
      // stock and then failed to answer, a blind retry is the one thing that
      // must not happen. Reloading shows the truth.
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not generate the bill. Reload this screen before trying again.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (bill) {
    return (
      <Page
        title={`Bill ${bill.reference}`}
        subtitle={`${order?.bill && !busy ? "Already billed" : "Generated"} · ${bill.customerName}`}
        width="wide"
      >
        <BillView bill={bill} />
        <Button size="md" onClick={() => navigate("/sales/queue")}>
          Back to the queue
        </Button>
      </Page>
    );
  }

  /**
   * Generating asks first, and states the two things that cannot be taken
   * back: the stock that leaves, and that the bill cannot be raised twice.
   * On a zero-value bill that is the consequence easiest to miss - the total
   * says nothing happened, and a real battery leaves the shelf.
   */
  function confirmGenerate() {
    if (!order || !preview) return;
    const units = order.lineItems.reduce((n, l) => n + l.quantityFulfilled, 0);
    modals.openConfirmModal({
      title: preview.billsZero ? "Generate a zero-value bill" : "Generate the bill",
      children: (
        <Stack gap="xs">
          <Text fz="md">
            {order.movesStockAtBilling
              ? `${units} unit(s) will be deducted from stock${
                  preview.billsZero ? ", even though the bill is zero" : ""
                }.`
              : "No stock moves for this order."}
          </Text>
          {/* Per SKU, before and after.
              
              Billing is the only thing in this app that moves stock, and a
              total of units does not say whether any particular shelf can
              stand it. A line that would go negative is called out here - the
              server refuses it either way, but being told at the point of
              commitment beats a refusal after pressing the button. */}
          {order.movesStockAtBilling && (
            <Stack gap={2}>
              {order.lineItems.map((line) => {
                const before = line.product.currentStockQty;
                const after = before - line.quantityFulfilled;
                return (
                  <Text key={line.id} fz="sm" c={after < 0 ? "red.8" : "gray.7"} className="num">
                    {line.product.sku}: {before} → {after}
                    {after < 0 ? " — not enough on the shelf" : ""}
                  </Text>
                );
              })}
            </Stack>
          )}
          <Text fz="md">
            The customer is billed {formatINR(preview.total)}. A bill cannot be
            undone or raised twice for the same order.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Generate the bill", cancel: "Not yet" },
      onConfirm: () => void generate(),
    });
  }

  return (
    <Page
      title="Generate bill"
      subtitle={order ? `${order.reference} · ${order.customer.name}` : undefined}
      back="/sales/orders"
      width="wide"
    >
      {error && !order && <ErrorState message={error} />}
      {error && order && <InlineError>{error}</InlineError>}
      {!order && !error && <LoadingSkeleton variant="detail" />}

      {order && preview && (
        <>
          <SectionCard title="Invoice to">
            <Stack gap={8} component="dl" m={0}>
              <Row label="Customer">{order.customer.name}</Row>
              <Row label="GSTIN">{order.customer.gstNumber ?? "Not registered"}</Row>
              <Row label="Place of supply">
                {order.placeOfSupply} — {preview.intraState ? "CGST + SGST" : "IGST"}
              </Row>
              {order.preOrder && (
                <Row label="From day order">{order.preOrder.reference}</Row>
              )}
            </Stack>
          </SectionCard>

          {preview.rows.map(({ line, gross, taxable, lineTax, excessive }) => (
            <SectionCard key={line.id} title={line.product.sku}>
              <Stack gap="sm">
                <Stack gap={2}>
                  <Text fz="lg" fw={600} c="gray.9">
                    {line.product.name}
                  </Text>
                  {line.product.hsnCode && (
                    <Text fz="sm" c="gray.6" className="num">
                      HSN {line.product.hsnCode}
                    </Text>
                  )}
                </Stack>

                <Stack gap={6} component="dl" m={0}>
                  <Row label="Quantity">
                    <span className="num">{line.quantityFulfilled}</span>
                  </Row>
                  <Row label="Unit price">
                    <span className="num">{formatINR(line.unitPrice)}</span>
                  </Row>
                  <Row label="Line value">
                    <span className="num">{formatINR(gross)}</span>
                  </Row>
                </Stack>

                {preview.billsZero ? (
                  <Text fz="sm" c="gray.6">
                    Nothing is charged for this order, so a discount would have
                    nothing to come off.
                  </Text>
                ) : preview.agreed ? (
                  <Text fz="sm" c="gray.6">
                    The Service Manager agreed{" "}
                    {formatINR(Number(order.prorataAmount ?? 0))} for this
                    pro-rata replacement, so the charge is already set. A
                    discount here would change a figure the customer has
                    already been given.
                  </Text>
                ) : mayDiscount ? (
                  <MoneyField
                    id={`disc-${line.id}`}
                    label="Discount"
                    description="Optional. Comes off this line before GST."
                    min={0}
                    placeholder="0.00"
                    value={discounts[line.id] ?? ""}
                    onChange={(value) =>
                      setDiscounts((current) => ({ ...current, [line.id]: value }))
                    }
                    error={
                      excessive
                        ? `A discount cannot exceed the line value of ${formatINR(gross)}.`
                        : undefined
                    }
                  />
                ) : (
                  <Text fz="sm" c="gray.6">
                    Only a Billing Executive or the Store Manager may enter a
                    discount.
                  </Text>
                )}

                <Stack gap={6}>
                  <Total label="Taxable" value={formatINR(taxable)} />
                  <Total
                    label={`GST at ${Number(line.gstRate)}%`}
                    value={formatINR(lineTax)}
                  />
                </Stack>
              </Stack>
            </SectionCard>
          ))}

          <SectionCard title="Preview">
            <Stack gap="md">
              {preview.billsZero && (
                <Alert
                  variant="light"
                  color="teal"
                  radius="md"
                  icon={<IconCircleCheck size={18} stroke={1.8} />}
                >
                  {order.type === "FOC"
                    ? "Free of charge — the customer pays nothing."
                    : "This order bills nothing."}
                </Alert>
              )}
              {preview.agreed && (
                <Alert
                  variant="light"
                  color="yellow"
                  radius="md"
                  icon={<IconAlertTriangle size={18} stroke={1.8} />}
                  title="Pro-rata replacement"
                >
                  The customer pays the{" "}
                  {formatINR(Number(order.prorataAmount ?? 0))} the Service
                  Manager agreed, plus GST — the rest of the batteries&apos;
                  value shows as the discount below.
                </Alert>
              )}

              <Stack gap={6}>
                <Total label="Sub total" value={formatINR(preview.subTotal)} />
                <Total
                  label="Discount"
                  value={
                    preview.discountTotal > 0
                      ? `− ${formatINR(preview.discountTotal)}`
                      : "—"
                  }
                />
                <Total label="Taxable value" value={formatINR(preview.taxable)} />
                {preview.intraState ? (
                  <>
                    <Total label="CGST" value={formatINR(preview.tax / 2)} />
                    <Total label="SGST" value={formatINR(preview.tax / 2)} />
                  </>
                ) : (
                  <Total label="IGST" value={formatINR(preview.tax)} />
                )}
                <Total label="Total" value={formatINR(preview.total)} strong />
              </Stack>
              <Text fz="sm" c="gray.6">
                Rounded for display. The bill is calculated in paise by the
                server, which may differ by a paisa.
              </Text>
            </Stack>
          </SectionCard>

          {/*
            WHOSE DECISION THIS IS, per order kind.
            
            On a SALE order this screen is where the agent is chosen, and
            billing is the right moment: somebody is looking at the order and
            knows where it is going.

            On a SERVICE order the Service Manager has already chosen, at the
            outcome step, and their name arrives pre-selected. This screen can
            still change it - the business asked for that, since the Billing
            Executive is the last person to see the order before it leaves -
            and the notes below say who chose what, so an override is visible
            rather than silent.

            Either way, an unassigned delivery is not lost: it waits in the
            Store Manager's pile. But an agent will not see it there, so
            naming somebody is what puts it onto a round.
          */}
          <SectionCard title="Delivery">
            <Stack gap="sm">
              <Select
                id="delivery-agent"
                label="Delivery agent"
                value={deliveryAgentId}
                onChange={(value) => setDeliveryAgentId(value ?? "")}
                allowDeselect={false}
                data={[
                  { value: "", label: "Leave for the Store Manager to assign" },
                  ...(agents ?? []).map((agent) => ({
                    value: agent.id,
                    // A plain Delivery Agent needs no qualifier; anyone else
                    // in this list is a manager covering a round, and saying
                    // so is the difference between a name and a decision.
                    label:
                      agent.role === "DELIVERY_AGENT"
                        ? agent.name
                        : `${agent.name} (${agent.role.replace(/_/g, " ").toLowerCase()})`,
                  })),
                ]}
              />
              {agents?.length === 0 && (
                <Text fz="sm" c="gray.6">
                  No delivery agents could be loaded. Generate the bill anyway
                  — the Store Manager can assign the delivery afterwards.
                </Text>
              )}
              {/* WHO CHOSE THIS, when it was not this screen.

                  Two people can name an agent on a service order now, and a
                  pre-filled dropdown gives no sign of which. Saying so is
                  what makes the override a decision rather than an accident -
                  the reader can see they are about to overrule somebody. */}
              {order.intendedDeliveryAgent &&
                deliveryAgentId === order.intendedDeliveryAgent.id && (
                  <Text fz="sm" c="gray.6">
                    The Service Manager asked for{" "}
                    {order.intendedDeliveryAgent.name} when they carried out
                    the outcome on {order.serviceJob?.reference ?? "this job"}.
                    Change it if somebody else is going.
                  </Text>
                )}
              {order.intendedDeliveryAgent &&
                order.intendedDeliveryAgent.status !== "ACTIVE" && (
                  <Text fz="sm" c="yellow.8">
                    The Service Manager asked for{" "}
                    {order.intendedDeliveryAgent.name}, whose account is no
                    longer active. Pick somebody else, or leave it for the
                    Store Manager.
                  </Text>
                )}
              {order.serviceJob?.collectionAgent &&
                deliveryAgentId === order.serviceJob.collectionAgent.id &&
                order.intendedDeliveryAgent?.id !==
                  order.serviceJob.collectionAgent.id && (
                  <Text fz="sm" c="gray.6">
                    {order.serviceJob.collectionAgent.name} collected this
                    battery for {order.serviceJob.reference}, so they have
                    already been to this address.
                  </Text>
                )}
              {!deliveryAgentId && (
                <Text fz="sm" c="gray.6">
                  Nobody is assigned yet, so this will wait in the Store
                  Manager&apos;s list rather than appearing on an
                  agent&apos;s round.
                </Text>
              )}
            </Stack>
          </SectionCard>

          {order.movesStockAtBilling && (
            <SectionCard title="What billing moves">
              <Stack gap={6}>
                {order.lineItems.map((line) => {
                  const before = line.product.currentStockQty;
                  const after = before - line.quantityFulfilled;
                  return (
                    <Group key={line.id} justify="space-between" wrap="nowrap" gap="sm">
                      <Stack gap={0} style={{ minWidth: 0 }}>
                        <Text fz="sm" fw={600} className="num" truncate>
                          {line.product.sku}
                        </Text>
                        <Text fz={12} c="gray.6" truncate>
                          {line.product.name}
                        </Text>
                      </Stack>
                      <Text
                        fz="md"
                        fw={600}
                        className="num"
                        c={after < 0 ? "red.8" : undefined}
                        style={{ flexShrink: 0 }}
                      >
                        {before} → {after}
                      </Text>
                    </Group>
                  );
                })}
                {/* The rule, stated where the consequence is shown. */}
                <Text fz={12} c="gray.6">
                  Generating the bill is the only thing in this app that moves
                  stock. It writes one ledger entry per line and cannot be
                  undone — a mistake is fixed with a correction, which leaves
                  both entries visible.
                </Text>
              </Stack>
            </SectionCard>
          )}

          <StickyActionBar
            context={
              order.movesStockAtBilling
                ? `Deducts ${order.lineItems.reduce(
                    (n, l) => n + l.quantityFulfilled,
                    0,
                  )} unit(s) from stock`
                : "No stock moves"
            }
            primary={
              <Button
                size="md"
                fullWidth
                disabled={busy || preview.excessive}
                loading={busy}
                onClick={confirmGenerate}
              >
                {preview.billsZero ? "Generate zero-value bill" : "Generate bill"}
              </Button>
            }
          />
        </>
      )}
    </Page>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} ta="right">
        {children}
      </Text>
    </Group>
  );
}

function Total({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Group
      justify="space-between"
      gap="md"
      wrap="nowrap"
      pt={strong ? "xs" : 0}
      style={
        strong ? { borderTop: "1px solid var(--mantine-color-gray-3)" } : undefined
      }
    >
      <Text fz={strong ? "md" : "sm"} c={strong ? "gray.9" : "gray.6"}>
        {label}
      </Text>
      <Text
        fz={strong ? "lg" : "sm"}
        fw={strong ? 700 : 500}
        c="gray.9"
        className="num"
      >
        {value}
      </Text>
    </Group>
  );
}
